import Typography from '@elementor/ui/Typography';
import { useRef } from 'react';
import AppWrapper from '@components/app-wrapper';
import { ERROR_CONTENT, ERROR_TYPE_MAP } from '@constants';
import { useScannerWizardContext } from '@context/scanner-wizard-context';
import { LoadingImage } from '@images';
import { StyledMessage } from '@styles/app.styles';
import { __ } from '@utils/i18n';
import { injectIframeStyles } from '@utils/inject-iframe-styles';

const App = () => {
	const {
		proxyError,
		loading,
		pageURL,
		pageHTML,
		getResults,
		setLoading,
		setPageDocument,
	} = useScannerWizardContext();
	const iframeRef = useRef(null);

	const { Image: ErrorImage, title, message } = ERROR_TYPE_MAP[proxyError] || ERROR_CONTENT.GENERIC_ERROR;

	if (proxyError) {
		return (
			<AppWrapper>
				<StyledMessage>
					<ErrorImage />
					<Typography as="h1">{title}</Typography>
					<Typography as="p">{message}</Typography>
				</StyledMessage>
			</AppWrapper>
		);
	}

	if (loading && !pageHTML) {
		return (
			<AppWrapper>
				<StyledMessage type="loading">
					<LoadingImage />

					<Typography as="h1">{__('Analyzing your site...')}</Typography>
					<Typography as="p">{__('We’ll have your results soon.')}</Typography>
				</StyledMessage>
			</AppWrapper>
		);
	}

	return (
		<AppWrapper>
			{pageHTML ? (
				<iframe
					title={pageURL}
					ref={iframeRef}
					srcDoc={pageHTML}
					sandbox="allow-same-origin allow-scripts"
					style={{ width: '100%', height: '100%', border: 0 }}
					onLoad={async () => {
						try {
							const contentDocument = iframeRef.current?.contentDocument;
							const contentWindow = iframeRef.current?.contentWindow;

							if (contentDocument && contentWindow) {
								contentDocument.addEventListener(
									'click',
									(e) => {
										const link = e.target.closest('a');

										if (link) {
											e.preventDefault();
											e.stopPropagation();
										}
									},
									true,
								);

								contentDocument.addEventListener(
									'submit',
									(e) => {
										e.preventDefault();
										e.stopPropagation();
									},
									true,
								);

								contentWindow.history.pushState = () => {};
								contentWindow.history.replaceState = () => {};

								injectIframeStyles();
								setPageDocument(contentDocument);
								await getResults(contentDocument);
							}
						} finally {
							setLoading(false);
						}
					}}
				/>
			) : null}
		</AppWrapper>
	);
};

export default App;
